import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
// Brand typefaces — variable fonts so a single import covers every weight the
// design tokens consume (Inter 400/500/600/700, Source Serif 4 400/600,
// JetBrains Mono 400/500). Self-hosted via @fontsource-variable so the app
// works in disconnected dev / Azure Gov (no Google Fonts CDN). The CSS
// custom properties `--font-ui` / `--font-serif` / `--font-mono` declared in
// `globals.css` reference these families.
import '@fontsource-variable/inter';
import '@fontsource-variable/source-serif-4';
import '@fontsource-variable/jetbrains-mono';
import { routeTree } from './routeTree.gen';
import { ApiError } from './lib/api/client';
import { initAuth } from './lib/auth/msal';
import { NotFound } from './components/layout/not-found';
import './globals.css';

/**
 * App entry. The TanStack Router file-based plugin generates the
 * `routeTree.gen.ts` import above from the `src/routes/` tree at dev /
 * build time. The QueryClient is configured to surface API errors
 * eagerly rather than silently retrying — the API has its own retry
 * semantics on the worker side; the UI prefers to show a clear failure
 * to the user.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Don't retry 4xx — they're caller errors.
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // A miss under a layout route (an unknown tab inside an engagement) is
  // rendered by the deepest matched layout, which has no not-found page of
  // its own; without this it showed the router's bare "Not Found" (C2-46).
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

// Resolve any Entra redirect + hydrate the session BEFORE the first render so a
// returning user lands authenticated (no flash of the login page). initAuth is
// a no-op when MSAL isn't configured (local dev), so this is safe everywhere.
void initAuth().finally(() => {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
